<template>
  <div>
    <el-dialog
      v-el-drag-dialog
      :visible.sync="innerVisible"
      :title="$t('Message.SelectCopyContent')"
      width="760px"
      :close-on-click-modal="false"
      append-to-body
    >
      <div class="sea-booking-copy-window">
        <div class="scheme-toolbar">
          <el-button size="mini" type="primary" @click="openSaveSchemeDialog">{{ $t('CargoMsg.Cargo_SaveAsScheme') }}</el-button>
          <span class="scheme-current">当前方案：{{ currentSchemeName }}</span>
          <el-button size="mini" type="primary" plain @click="schemeSelectorVisible=true">复制方案选择</el-button>
        </div>

        <fieldset class="copy-fieldset">
          <legend>{{ $t('Message.CopyContent') }}</legend>
          <div class="basic-grid">
            <el-checkbox
              v-for="item in basicItems"
              :key="item.itemKey"
              :value="isItemSelected(item.itemKey)"
              :disabled="item.itemKey === 'basicInfo'"
              @change="onItemSelectionChange(item.itemKey, $event)"
            >{{ renderItemLabel(item) }}</el-checkbox>
          </div>

          <div class="copy-divider" />

          <div class="detail-grid">
            <el-checkbox
              v-for="item in detailItems"
              :key="item.itemKey"
              :value="isItemSelected(item.itemKey)"
              @change="onItemSelectionChange(item.itemKey, $event)"
            >{{ renderItemLabel(item) }}</el-checkbox>
          </div>
        </fieldset>

        <el-checkbox v-model="options.notMatchAgainSelect" class="copy-tip">
          <span>{{ $t('queryJob.WhenDepartmentBranchNewSelection') }}</span>
        </el-checkbox>

        <div class="copy-number-row">
          <label>{{ $t('Message.CopyNumber') }}</label>
          <el-input-number
            :value="options.copyNum"
            :precision="0"
            :min="1"
            :max="100"
            controls-position="right"
            @input="onCopyNumInput"
          />
        </div>
      </div>

      <span slot="footer">
        <el-button type="primary" @click="handleConfirm">{{ $t('common.btn.confirm') }}</el-button>
        <el-button @click="innerVisible=false">{{ $t('common.btn.cancel') }}</el-button>
      </span>
    </el-dialog>

    <el-dialog
      v-el-drag-dialog
      :visible.sync="schemeSelectorVisible"
      title="复制方案选择"
      width="420px"
      append-to-body
      :close-on-click-modal="false"
    >
      <div class="scheme-selector-list">
        <div
          v-for="scheme in schemeList"
          :key="schemeKey(scheme)"
          class="scheme-selector-item"
        >
          <span class="scheme-selector-name">
            {{ scheme.configName }}
            <span v-if="isSchemeDefault(scheme)" class="scheme-default-tag">默认</span>
          </span>
          <div class="scheme-selector-actions">
            <el-button size="mini" @click="selectScheme(scheme)">使用</el-button>
            <el-button
              v-if="!scheme.builtin && !isSchemeDefault(scheme)"
              size="mini"
              type="primary"
              plain
              @click="setSchemeAsDefault(scheme)"
            >设为默认</el-button>
            <el-button
              v-if="!scheme.builtin"
              size="mini"
              type="danger"
              plain
              @click="deleteScheme(scheme)"
            >删除</el-button>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="schemeSelectorVisible=false">{{ $t('common.btn.cancel') }}</el-button>
      </span>
    </el-dialog>

    <el-dialog
      v-el-drag-dialog
      :visible.sync="saveSchemeDialog.visible"
      :title="$t('CargoMsg.Cargo_SaveAsScheme')"
      width="360px"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-form label-position="top">
        <el-form-item label="方案名称">
          <el-input v-model.trim="saveSchemeDialog.name" maxlength="50" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button type="primary" @click="saveAsScheme">{{ $t('common.btn.confirm') }}</el-button>
        <el-button @click="saveSchemeDialog.visible=false">{{ $t('common.btn.cancel') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import isEqual from 'lodash/isEqual'
import LocalStorageUtils from '@/utils/localStorageUtils'
import LocalStorageUtilsNew from '@/utils/localStorageUtilsNew'
import { getGlobalQueryConfig, getPersonQueryConfig, savePersonQueryConfig, setDefault, delPersonQueryConfig } from '@/api/commonComponent'
import {
  SEA_BOOKING_COPY_CONFIG_MODULE,
  SEA_BOOKING_COPY_OPTIONS_CACHE_PREFIX,
  SEA_BOOKING_COPY_BASIC_ITEM_KEYS,
  createDefaultScheme,
  parseGlobalCopyFieldConfig,
  normalizeSchemeContent,
  parsePersonalScheme,
  resolveInitialScheme,
  isCopyFieldSelected,
  setCopyFieldSelected
} from './seaBookingCopyConfig'

export default {
  name: 'SeaBookingCopyWindow',
  props: {
    visible: { type: Boolean, required: true },
    lob: { type: String, default: '' },
    isMaster: { type: Boolean, default: false }
  },
  data() {
    return {
      innerVisible: false,
      schemeSelectorVisible: false,
      globalCopyFieldConfig: [],
      schemeList: [createDefaultScheme([])],
      currentScheme: createDefaultScheme([]),
      options: normalizeSchemeContent({}, []),
      saveSchemeDialog: {
        visible: false,
        name: ''
      }
    }
  },
  computed: {
    cacheSuffix() {
      return this.isMaster ? `${this.lob}_MASTER` : this.lob
    },
    basicItems() {
      return this.options.copyFieldConfig.filter(item => SEA_BOOKING_COPY_BASIC_ITEM_KEYS.includes(item.itemKey))
    },
    detailItems() {
      return this.options.copyFieldConfig.filter(item => !SEA_BOOKING_COPY_BASIC_ITEM_KEYS.includes(item.itemKey))
    },
    currentSchemeName() {
      return this.currentScheme && this.currentScheme.configName ? this.currentScheme.configName : 'default'
    },
    personalSchemes() {
      return this.schemeList.filter(item => !item.builtin)
    }
  },
  watch: {
    visible(newVal) {
      this.innerVisible = newVal
      if (newVal) {
        this.init()
      }
    },
    innerVisible(newVal) {
      this.$emit('update:visible', newVal)
    }
  },
  created() {
    if (this.visible) {
      this.innerVisible = true
      this.init()
    }
  },
  methods: {
    async init() {
      this.schemeSelectorVisible = false
      this.saveSchemeDialog.visible = false
      this.saveSchemeDialog.name = ''
      try {
        const [globalRes, personRes] = await Promise.all([
          getGlobalQueryConfig(1, SEA_BOOKING_COPY_CONFIG_MODULE),
          getPersonQueryConfig({
            configType: 1,
            configModule: SEA_BOOKING_COPY_CONFIG_MODULE
          })
        ])
        this.globalCopyFieldConfig = parseGlobalCopyFieldConfig(globalRes.data && globalRes.data.configContent)
        const nextBuiltinScheme = createDefaultScheme(this.globalCopyFieldConfig)
        const personalSchemes = (personRes.data || []).map(item => parsePersonalScheme(item, this.globalCopyFieldConfig))
        this.schemeList = [nextBuiltinScheme, ...personalSchemes]
        this.applyScheme(resolveInitialScheme(personRes.data || [], nextBuiltinScheme, this.globalCopyFieldConfig))
      } catch (err) {
        const fallbackBuiltinScheme = createDefaultScheme(this.globalCopyFieldConfig)
        this.schemeList = [fallbackBuiltinScheme]
        this.applyScheme(fallbackBuiltinScheme)
        this.$message.error('复制方案加载失败，已回退为 default')
      }
    },
    schemeKey(scheme) {
      return scheme.id || `builtin-${scheme.configName}`
    },
    isSchemeDefault(scheme) {
      return scheme && (scheme.bDefault === true || scheme.bDefault === 1 || scheme.bDefault === '1')
    },
    applyScheme(scheme) {
      this.currentScheme = scheme
      this.options = normalizeSchemeContent(scheme.content, this.globalCopyFieldConfig)
    },
    selectScheme(scheme) {
      this.applyScheme(scheme)
      this.schemeSelectorVisible = false
    },
    isItemSelected(itemKey) {
      return isCopyFieldSelected(this.options, itemKey)
    },
    onItemSelectionChange(itemKey, selected) {
      this.options = setCopyFieldSelected(this.options, itemKey, selected)
    },
    renderItemLabel(item) {
      const translated = this.$t(item.itemLabel)
      return translated === item.itemLabel ? item.itemLabel : translated
    },
    onCopyNumInput(val) {
      this.options.copyNum = typeof val === 'undefined' ? 1 : val
    },
    openSaveSchemeDialog() {
      this.saveSchemeDialog.name = ''
      this.saveSchemeDialog.visible = true
    },
    async saveAsScheme() {
      const name = (this.saveSchemeDialog.name || '').trim()
      if (!name) {
        this.$message.error('请输入方案名称')
        return
      }
      const existingScheme = this.personalSchemes.find(item => item.configName === name)
      const id = existingScheme ? existingScheme.id : undefined
      if (existingScheme) {
        try {
          await this.$confirm('方案已存在，是否覆盖原方案？', this.$t('Message.Prompt'), {
            type: 'warning'
          })
        } catch (err) {
          return
        }
      }
      await this.persistScheme({
        id,
        configName: name,
        bDefault: existingScheme ? existingScheme.bDefault : false
      })
      this.saveSchemeDialog.visible = false
    },
    async setSchemeAsDefault(scheme) {
      const currentDefault = this.personalSchemes.find(item => this.isSchemeDefault(item))
      await setDefault({
        newId: scheme.id,
        oldId: currentDefault ? currentDefault.id : ''
      })
      this.$message.success(this.$t('CargoMsg.Cargo_SchemeSaveSuccess'))
      await this.reloadSchemes(scheme.configName)
      this.schemeSelectorVisible = false
    },
    async deleteScheme(scheme) {
      if (!scheme || !scheme.id) {
        return
      }
      try {
        await this.$confirm(`确定删除方案【${scheme.configName}】吗？`, this.$t('Message.Prompt'), {
          type: 'warning'
        })
      } catch (err) {
        return
      }
      await delPersonQueryConfig(scheme.id)
      this.$message.success('删除成功')
      await this.reloadSchemes()
    },
    async reloadSchemes(preferredName) {
      const builtinScheme = createDefaultScheme(this.globalCopyFieldConfig)
      const res = await getPersonQueryConfig({
        configType: 1,
        configModule: SEA_BOOKING_COPY_CONFIG_MODULE
      })
      const personalSchemes = (res.data || []).map(item => parsePersonalScheme(item, this.globalCopyFieldConfig))
      this.schemeList = [builtinScheme, ...personalSchemes]
      const preferredScheme = this.schemeList.find(item => item.configName === preferredName)
      if (preferredScheme) {
        this.applyScheme(preferredScheme)
        return
      }
      this.applyScheme(resolveInitialScheme(res.data || [], builtinScheme, this.globalCopyFieldConfig))
    },
    async persistScheme({ id, configName, bDefault = false }) {
      await savePersonQueryConfig({
        id,
        bDefault,
        configType: 1,
        configModule: SEA_BOOKING_COPY_CONFIG_MODULE,
        configName,
        configContent: JSON.stringify(this.options)
      })
      this.$message.success(this.$t('CargoMsg.Cargo_SchemeSaveSuccess'))
      await this.reloadSchemes(configName)
    },
    syncLegacyCopyConfig() {
      LocalStorageUtilsNew.setConfiguration('NOT_MATCH_AGAIN_SELECT_' + this.cacheSuffix, this.options.notMatchAgainSelect)
      LocalStorageUtilsNew.setConfiguration(SEA_BOOKING_COPY_OPTIONS_CACHE_PREFIX + this.cacheSuffix, this.options)
      LocalStorageUtils.setConfiguration('COPY_VESSEL_EQ_' + this.cacheSuffix, this.isItemSelected('cargoInfo'))
      LocalStorageUtils.setConfiguration('COPY_MBL_' + this.cacheSuffix, this.isItemSelected('mbl'))
      LocalStorageUtils.setConfiguration('COPY_HBL_' + this.cacheSuffix, this.isItemSelected('hbl'))
      LocalStorageUtils.setConfiguration('COPY_CHARGE_' + this.cacheSuffix, this.isItemSelected('charge'))
      LocalStorageUtils.setConfiguration('COPY_PRE_PLAN_BOOKING_' + this.cacheSuffix, this.isItemSelected('prePlanBooking'))
      LocalStorageUtils.setConfiguration('COPY_NUM_INPUT' + this.cacheSuffix, String(this.options.copyNum))
    },
    async maybePersistCurrentScheme() {
      if (this.currentScheme.builtin) {
        return
      }
      if (isEqual(this.currentScheme.content, this.options)) {
        return
      }
      await this.persistScheme({
        id: this.currentScheme.id,
        configName: this.currentScheme.configName,
        bDefault: this.currentScheme.bDefault
      })
    },
    async handleConfirm() {
      try {
        await this.maybePersistCurrentScheme()
      } catch (err) {
        this.$message.error(err && err.message ? err.message : '复制方案保存失败')
        return
      }
      this.syncLegacyCopyConfig()
      this.$emit('confirmCallBack', {
        options: { ...this.options },
        scheme: this.currentScheme
      })
      this.innerVisible = false
    }
  }
}
</script>

<style scoped lang="scss">
.sea-booking-copy-window {
  .scheme-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .scheme-current {
    flex: 1;
    text-align: center;
    font-size: 14px;
    color: #333;
  }

  .copy-fieldset {
    border: 1px solid #dcdfe6;
    padding: 8px 12px 12px;
    margin: 0;
  }

  .basic-grid,
  .detail-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-column-gap: 16px;
    grid-row-gap: 10px;
  }

  .copy-divider {
    height: 1px;
    background: #dcdfe6;
    margin: 12px 0;
  }

  .copy-tip {
    display: flex;
    margin-top: 10px;
    white-space: normal;
    line-height: 18px;
  }

  .copy-number-row {
    display: flex;
    align-items: center;
    margin-top: 12px;

    label {
      margin-right: 12px;
    }
  }
}

.scheme-selector-list {
  max-height: 360px;
  overflow-y: auto;
}

.scheme-selector-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #ebeef5;
}

.scheme-selector-name {
  color: #303133;
}

.scheme-default-tag {
  margin-left: 8px;
  font-size: 12px;
  color: #409eff;
}

.scheme-selector-actions .el-button + .el-button {
  margin-left: 8px;
}
</style>
